import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Navbar.css';

const Navbar = ({ darkMode, toggleDarkMode }) => {
    const navigate = useNavigate();
    const [isScrolled, setIsScrolled] = useState(false);
    const [lastScroll, setLastScroll] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const [isExpertiseOpen, setIsExpertiseOpen] = useState(false);
    const [isMobileExpertiseOpen, setIsMobileExpertiseOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const expertiseRef = useRef(null);
    const dropdownRef = useRef(null);
    const searchRef = useRef(null);
    const mobileMenuRef = useRef(null);
    const hamburgerRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            const currentScroll = window.pageYOffset;
            setIsScrolled(currentScroll > 50);
            setIsVisible(currentScroll <= lastScroll || currentScroll <= 100);
            setLastScroll(currentScroll);
        };

        const handleClickOutside = (event) => {
            if (expertiseRef.current && !expertiseRef.current.contains(event.target)) {
                setIsExpertiseOpen(false);
            }
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setIsSearchOpen(false);
                setSearchQuery('');
            }
            if (
                mobileMenuRef.current &&
                !mobileMenuRef.current.contains(event.target) &&
                hamburgerRef.current &&
                !hamburgerRef.current.contains(event.target)
            ) {
                setIsMobileMenuOpen(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [lastScroll]);

    const servicesData = [
        {
            title: "ElyConcierge",
            description: "WhatsApp-based guest communication for hospitality",
            link: "/elyconcierge",
            icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
            )
        },
        {
            title: "ElyFleet",
            description: "Real-time vehicle tracking and fleet management system",
            link: "/elyfleet",
            icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="3" width="15" height="13" rx="1" />
                    <polygon points="16 8 20 8 23 11 23 16 16 16 8" />
                    <circle cx="5.5" cy="18.5" r="2.5" />
                    <circle cx="18.5" cy="18.5" r="2.5" />
                </svg>
            )
        },
        {
            title: "ElyDine",
            description: "Restaurant management with QR ordering and billing",
            link: "/elydine",
            icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2M7 2v20M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/>
                </svg>
            )
        },
        {
            title: "ElyVentory",
            description: "Inventory management with barcode tracking",
            link: "/elyventory",
            icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                    <line x1="12" y1="22.08" x2="12" y2="12"/>
                </svg>
            )
        },
        {
            title: "ElyCustomized",
            description: "Custom solutions tailored to your business needs",
            link: "/elycustomized",
            hidden: true,
            icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                </svg>
            )
        }
    ];

    const visibleServices = servicesData.filter((service) => !service.hidden);

    const menuItems = [
        {
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="4"/>
                    <polyline points="9 12 12 15 17 10"/>
                </svg>
            ),
            title: "Attendance System",
            actionLink: "/attendance",
            actionText: "Boost Productivity"
        },
        {
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="14" rx="2"/>
                    <path d="M3 8h18"/>
                    <circle cx="7" cy="7" r="1"/>
                    <circle cx="11" cy="7" r="1"/>
                </svg>
            ),
            title: "Website Making",
            actionLink: "/attendance",
            actionText: "Build My Website"
        },
        {
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    <circle cx="9" cy="10" r="1"/>
                    <circle cx="15" cy="10" r="1"/>
                </svg>
            ),
            title: "Chatbot Integration",
            actionLink: "/attendance",
            actionText: "Automate My Chat"
        },
        {
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="13" width="7" height="7" rx="1"/>
                    <rect x="14" y="13" width="7" height="7" rx="1"/>
                    <rect x="8.5" y="3" width="7" height="7" rx="1"/>
                </svg>
            ),
            title: "Inventory Management",
            actionLink: "/inventory",
            actionText: "Streamline Inventory"
        }
    ];

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            setIsDropdownOpen(!isDropdownOpen);
        }
        if (e.key === 'Escape') {
            setIsDropdownOpen(false);
            setIsMobileMenuOpen(false);
        }
    };

    const handleSearch = (value) => {
        setSearchQuery(value);
        setIsDropdownOpen(true);
    };

    const filteredItems = menuItems.filter(item =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.actionText.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <nav
            className={`navbar ${isScrolled ? 'scrolled' : ''}`}
            style={{ transform: 'translateX(-50%) translateY(0)' }}
        >
            <Link to="/" className="logo">
                <img 
                    src={darkMode ? "/logo3.avif" : "/logo-1.avif"} 
                    alt="Logo" 
                    className="navbar-logo" 
                    style={{
                        height: '42px',
                        width: 'auto',
                        objectFit: 'contain'
                    }} 
                />
            </Link>

            {/* Mobile Menu Button */}
            <button 
                ref={hamburgerRef}
                className={`mobile-menu-button ${isMobileMenuOpen ? 'open' : ''}`}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle mobile menu"
            >
                <span></span>
                <span></span>
                <span></span>
            </button>

            {/* Navigation Links */}
            <div className={`nav-links ${isMobileMenuOpen ? 'mobile-open' : ''}`} ref={mobileMenuRef}>
                {/* Expertise Button - Desktop Only */}
                <div className="expertise-dropdown desktop-only" ref={expertiseRef}>
                    <button 
                        className={`nav-link expertise-trigger ${isExpertiseOpen ? 'open' : ''}`}
                        onClick={() => setIsExpertiseOpen(!isExpertiseOpen)}
                        aria-label="Toggle services menu"
                    >
                        Services
                        <span className={`dropdown-arrow ${isExpertiseOpen ? 'open' : ''}`}>▼</span>
                    </button>
                    <div className={`expertise-mega-menu ${isExpertiseOpen ? 'opening' : 'closing'}`}> 
                        <div className="mega-menu-content">
                            {visibleServices.map((service, index) => (
                                <Link 
                                    key={index}
                                    to={service.link} 
                                    className="service-item"
                                    onClick={() => {
                                        // Close menus first
                                        setIsExpertiseOpen(false);
                                        setIsMobileMenuOpen(false);
                                        // Ensure navigation happens even if an overlay blocks the native link click
                                        try {
                                            navigate(service.link);
                                        } catch (err) {
                                            // swallow any navigate error; Link should still work
                                            // console.warn('navigate failed', err);
                                        }
                                    }}
                                >
                                    <div className="service-icon">{service.icon}</div>
                                    <div className="service-content">
                                        <h4>{service.title}</h4>
                                        <p>{service.description}</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Services Button - Mobile Only */}
                <a
                    href="#"
                    className="nav-link mobile-only"
                    onClick={e => { e.preventDefault(); setIsMobileExpertiseOpen(prev => !prev); }}
                    aria-label="Show services"
                >
                    Services
                    <span className={`dropdown-arrow ${isMobileExpertiseOpen ? 'open' : ''}`}>▼</span>
                </a>
                                {isMobileExpertiseOpen && isMobileMenuOpen && (
                                    <div
                                        className="mobile-expertise-list mobile-only"
                                        style={{ width: '100%', marginBottom: '0.5rem', background: 'none', borderRadius: 0, boxShadow: 'none', padding: 0 }}
                                    >
                                        {visibleServices.map((service, idx) => (
                                            <Link
                                                key={idx}
                                                to={service.link}
                                                className="mobile-expertise-item"
                                                style={{ display: 'block', width: '100%', padding: '0.3rem 0', textAlign: 'left', fontWeight: 'inherit', fontSize: 'inherit', color: 'inherit', textDecoration: 'none', border: 'none', background: 'none' }}
                                                onClick={() => {
                                                    setIsMobileExpertiseOpen(false);
                                                    setIsMobileMenuOpen(false);
                                                    try {
                                                        navigate(service.link);
                                                    } catch (err) {
                                                        // ignore - Link navigation should handle it
                                                    }
                                                }}
                                            >
                                                {service.title}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                
                <Link to="/contact" className="nav-link" onClick={() => { setIsMobileMenuOpen(false); setIsMobileExpertiseOpen(false); }}>Connect</Link>
                
                {/* Dark Mode Toggle - Desktop */}
                <button
                    className="darkmode-toggle desktop-only"
                    onClick={toggleDarkMode}
                    aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                    {darkMode ? (
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 12.79A9 9 0 1 1 11.21 3A7 7 0 0 0 21 12.79Z" />
                        </svg>
                    ) : (
                        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="14" cy="14" r="5" />
                          <line x1="14" y1="2" x2="14" y2="5" />
                          <line x1="14" y1="23" x2="14" y2="26" />
                          <line x1="2" y1="14" x2="5" y2="14" />
                          <line x1="23" y1="14" x2="26" y2="14" />
                          <line x1="6.5" y1="6.5" x2="8.5" y2="8.5" />
                          <line x1="21.5" y1="21.5" x2="19.5" y2="19.5" />
                          <line x1="6.5" y1="21.5" x2="8.5" y2="19.5" />
                          <line x1="21.5" y1="6.5" x2="19.5" y2="8.5" />
                        </svg>
                    )}
                </button>
            </div>

            {/* Dark Mode Toggle - Mobile */}
            <button
                className="darkmode-toggle mobile-only"
                onClick={toggleDarkMode}
                aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
                {darkMode ? (
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12.79A9 9 0 1 1 11.21 3A7 7 0 0 0 21 12.79Z" />
                    </svg>
                ) : (
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="14" cy="14" r="5" />
                      <line x1="14" y1="2" x2="14" y2="5" />
                      <line x1="14" y1="23" x2="14" y2="26" />
                      <line x1="2" y1="14" x2="5" y2="14" />
                      <line x1="23" y1="14" x2="26" y2="14" />
                      <line x1="6.5" y1="6.5" x2="8.5" y2="8.5" />
                      <line x1="21.5" y1="21.5" x2="19.5" y2="19.5" />
                      <line x1="6.5" y1="21.5" x2="8.5" y2="19.5" />
                      <line x1="21.5" y1="6.5" x2="19.5" y2="8.5" />
                    </svg>
                )}
            </button>
        </nav>
    );
};

export default Navbar;
